import { useToast } from '@/hooks/use-toast'
import { uploadImages } from '@/lib/upload'
import { cn } from '@/lib/utils'
import type { RSVPResponse } from '@/shared/types/rsvp'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Send } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { ImageUpload, type ImageFile } from './ImageUpload'
import { Button } from './ui/button'
import { Checkbox } from './ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form'
import { Input } from './ui/input'
import { RadioGroup, RadioGroupItem } from './ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select'
import { Textarea } from './ui/textarea'

const formSchema = z.object({
  name: z.string().min(1, 'お名前を入力してください'),
  receptionAttendance: z.enum(['yes', 'no']),
  afterPartyAttendance: z.enum(['yes', 'no']),
  afterPartyCount: z.number().min(0).max(10).optional(),
  plusOne: z.number().min(0).max(5),
  plusOneNames: z.string().optional(),
  children: z.number().min(0).max(5),
  childrenNames: z.string().optional(),
  childrenMealPreference: z.record(z.boolean()).optional(),
  allergies: z.record(z.string()).optional(),
  allergyCheckboxes: z.record(z.boolean()).optional(),
  message: z.string().optional(),
  images: z.array(z.string()).optional(),
})

type FormData = z.infer<typeof formSchema>

export default function RSVPFormInline() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([])
  const [uploadProgress, setUploadProgress] = useState<{[key: number]: number}>({})
  const [uploadingImages, setUploadingImages] = useState(false)
  const { toast } = useToast()

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      receptionAttendance: 'yes',
      afterPartyAttendance: 'yes',
      afterPartyCount: 1,
      plusOne: 0,
      plusOneNames: '',
      children: 0,
      childrenNames: '',
      childrenMealPreference: {},
      allergies: {},
      allergyCheckboxes: {},
      message: '',
      images: [],
    },
  })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    let uploadedImageUrls: string[] = []

    try {
      // 画像がある場合はアップロード
      if (imageFiles.length > 0) {
        setUploadingImages(true)
        setUploadProgress({})

        const uploadResult = await uploadImages({
          guestId: data.name,
          files: imageFiles.map(f => f.file),
          onProgress: (fileIndex, progress) => {
            setUploadProgress(prev => ({
              ...prev,
              [fileIndex]: progress
            }))
          },
        })

        if (uploadResult.failed.length > 0) {
          toast({
            title: "画像アップロードの一部が失敗しました",
            description: `${uploadResult.failed.length}枚の画像のアップロードに失敗しました。`,
            variant: "destructive",
          })
        }

        uploadedImageUrls = uploadResult.successful.map(r => r.imageUrl)
        setUploadingImages(false)
      }

      const submitData = {
        ...data,
        images: uploadedImageUrls,
      }

      const response = await fetch('/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      })

      const result = await response.json() as RSVPResponse

      if (result.success) {
        setSuccess(true)
        toast({
          title: "送信完了",
          description: "出欠のご連絡を受け付けました。",
        })
        setTimeout(() => {
          setSuccess(false)
          form.reset()
          // 画像のObject URLをクリーンアップ
          imageFiles.forEach(file => URL.revokeObjectURL(file.preview))
          setImageFiles([])
          setUploadProgress({})
        }, 3000)
      } else {
        toast({
          title: "エラー",
          description: result.message || 'エラーが発生しました',
          variant: "destructive",
        })
      }
    } catch {
      toast({
        title: "送信エラー",
        description: '送信に失敗しました。もう一度お試しください。',
        variant: "destructive",
      })
    } finally {
      setLoading(false)
      setUploadingImages(false)
    }
  }

  if (success) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-xl border-2 border-pastel-purple">
          <div className="text-center py-8">
            <div className="text-6xl mb-4 animate-wiggle">🎉</div>
            <h3 className="text-2xl font-fairy mb-2">ありがとうございます！</h3>
            <p className="text-gray-600">
              出欠のご連絡を受け付けました。<br />
              当日お会いできることを楽しみにしています。
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-blue-to-green rounded-3xl p-4 md:p-8 shadow-xl border-4 border-white/50 relative overflow-hidden">
        {/* ちぇるちゃ */}
        <div className="absolute inset-0 opacity-50 pointer-events-none">
          <img width={180} height={180} src="/cats/chanyaSleep.webp" alt="chanyaSleep" className="absolute top-24 right-6 transform animate-float-delayed" />
          <img width={180} height={180} src="/cats/chelseaSleep.webp" alt="chelseaSleep" className="absolute bottom-10 right-6 transform animate-float-delayed" />
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 relative z-10">
            {/* 基本情報 */}
            <div className="space-y-4">
              {/* <h3 className="text-lg md:text-xl font-fairy text-gray-800 border-b-2 border-pastel-purple pb-2">
                基本情報
              </h3> */}

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>お名前 <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input {...field} className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="receptionAttendance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>披露宴への出欠 <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-wrap gap-2"
                      >
                        <label className="flex items-center space-x-3 cursor-pointer min-h-[44px] py-2 px-3 rounded-lg hover:bg-white/20 transition-colors">
                          <RadioGroupItem value="yes" />
                          <span className="font-normal">出席</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer min-h-[44px] py-2 px-3 rounded-lg hover:bg-white/20 transition-colors">
                          <RadioGroupItem value="no" />
                          <span className="font-normal">欠席</span>
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 同伴者情報 */}
            <div className="space-y-4">
              {/* <h3 className="text-lg md:text-xl font-fairy text-gray-800 border-b-2 border-pastel-purple pb-2">
                同伴者情報
              </h3> */}

              <FormField
                control={form.control}
                name="plusOne"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>同伴者数(👨‍🎓中学生以上)</FormLabel>
                    <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5].map(num => (
                          <SelectItem key={num} value={String(num)}>{num}名</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {Array.from({ length: form.watch('plusOne') || 0 }).map((_, index) => (
                <FormField
                  key={`plusOne-${index}`}
                  control={form.control}
                  name={`plusOneNames`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{index + 1}人目の同伴者のお名前</FormLabel>
                      <FormControl>
                        <Input
                          value={field.value?.split(',')[index] || ''}
                          onChange={(e) => {
                            const names = field.value?.split(',') || []
                            names[index] = e.target.value
                            field.onChange(names.filter(n => n).join(','))
                          }}
                          placeholder={`${index + 1}人目の同伴者のお名前`}
                          className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}

              <FormField
                control={form.control}
                name="children"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>お子様の人数(👼小学生以下)</FormLabel>
                    <div className="text-sm text-gray-600 mb-2">
                      ※ キッズメニューをご用意します！
                    </div>
                    <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                      <FormControl>
                        <SelectTrigger className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {[0, 1, 2, 3, 4, 5].map(num => (
                          <SelectItem key={num} value={String(num)}>{num}名</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {Array.from({ length: form.watch('children') || 0 }).map((_, index) => (
                <div key={`children-${index}`} className="space-y-2">
                  <FormField
                    control={form.control}
                    name={`childrenNames`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{index + 1}人目のお子様のお名前</FormLabel>
                        <FormControl>
                          <Input
                            value={field.value?.split(',')[index] || ''}
                            onChange={(e) => {
                              const names = field.value?.split(',') || []
                              names[index] = e.target.value
                              field.onChange(names.filter(n => n).join(','))
                            }}
                            placeholder={`${index + 1}人目のお子様のお名前`}
                            className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {form.watch('childrenNames')?.split(',')[index] && (
                    <FormField
                      control={form.control}
                      name="childrenMealPreference"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center space-x-2 ml-4">
                            <FormControl>
                              <Checkbox
                                checked={!!field.value?.[`children-${index}`]}
                                onCheckedChange={(checked) => {
                                  const newPreferences = { ...field.value }
                                  newPreferences[`children-${index}`] = checked as boolean
                                  field.onChange(newPreferences)
                                }}
                                className="bg-white"
                              />
                            </FormControl>
                            <FormLabel className="font-normal cursor-pointer">お子様用の食事を希望</FormLabel>
                          </div>
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              ))}

            </div>

            {/* その他 */}
            <div className="space-y-4">
              {/* <h3 className="text-lg md:text-xl font-fairy text-gray-800 border-b-2 border-pastel-purple pb-2">
                その他
              </h3> */}

              {/* 食物アレルギー情報 */}
              <div className="space-y-4">
                <FormLabel>食物アレルギーのある方</FormLabel>
                <div className="space-y-2">
                  {/* 本人のアレルギー */}
                  {form.watch('name') && (
                    <>
                      <FormField
                        control={form.control}
                        name="allergyCheckboxes"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center space-x-2">
                              <FormControl>
                                <Checkbox
                                  checked={!!field.value?.['main']}
                                  onCheckedChange={(checked) => {
                                    const newCheckboxes = { ...field.value }
                                    newCheckboxes['main'] = checked as boolean
                                    field.onChange(newCheckboxes)
                                  }}
                                  className="bg-white"
                                />
                              </FormControl>
                              <FormLabel className="font-normal cursor-pointer">{form.watch('name')}</FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                      {form.watch('allergyCheckboxes')?.['main'] && (
                        <FormField
                          control={form.control}
                          name="allergies"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  value={field.value?.['main'] || ''}
                                  onChange={(e) => {
                                    const newAllergies = { ...field.value }
                                    newAllergies['main'] = e.target.value
                                    field.onChange(newAllergies)
                                  }}
                                  placeholder="アレルギーの詳細を入力"
                                  className="mt-2 rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      )}
                    </>
                  )}

                    {/* 同伴者のアレルギー */}
                    {form.watch('plusOneNames')?.split(',').filter(n => n).map((name, index) => (
                      <div key={`plusOne-allergy-${index}`}>
                        <FormField
                          control={form.control}
                          name="allergyCheckboxes"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center space-x-2">
                                <FormControl>
                                  <Checkbox
                                    checked={!!field.value?.[`plusOne-${index}`]}
                                    onCheckedChange={(checked) => {
                                      const newCheckboxes = { ...field.value }
                                      newCheckboxes[`plusOne-${index}`] = checked as boolean
                                      field.onChange(newCheckboxes)
                                    }}
                                    className="bg-white"
                                  />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">{name}</FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                        {form.watch('allergyCheckboxes')?.[`plusOne-${index}`] && (
                          <FormField
                            control={form.control}
                            name="allergies"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input
                                    value={field.value?.[`plusOne-${index}`] || ''}
                                    onChange={(e) => {
                                      const newAllergies = { ...field.value }
                                      newAllergies[`plusOne-${index}`] = e.target.value
                                      field.onChange(newAllergies)
                                    }}
                                    placeholder="アレルギーの詳細を入力"
                                    className="mt-2 rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        )}
                      </div>
                    ))}

                    {/* 子供のアレルギー */}
                    {form.watch('childrenNames')?.split(',').filter(n => n).map((name, index) => {
                      if (!form.watch('childrenMealPreference')?.[`children-${index}`]) return null
                      return (
                        <div key={`children-allergy-${index}`} className="ml-6">
                          <FormField
                            control={form.control}
                            name="allergyCheckboxes"
                            render={({ field }) => (
                              <FormItem>
                                <div className="flex items-center space-x-2">
                                  <FormControl>
                                    <Checkbox
                                      checked={!!field.value?.[`children-${index}`]}
                                      onCheckedChange={(checked) => {
                                        const newCheckboxes = { ...field.value }
                                        newCheckboxes[`children-${index}`] = checked as boolean
                                        field.onChange(newCheckboxes)
                                      }}
                                      className="bg-white"
                                    />
                                  </FormControl>
                                  <FormLabel className="font-normal cursor-pointer">{name}</FormLabel>
                                </div>
                              </FormItem>
                            )}
                          />
                          {form.watch('allergyCheckboxes')?.[`children-${index}`] && (
                            <FormField
                              control={form.control}
                              name="allergies"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      value={field.value?.[`children-${index}`] || ''}
                                      onChange={(e) => {
                                        const newAllergies = { ...field.value }
                                        newAllergies[`children-${index}`] = e.target.value
                                        field.onChange(newAllergies)
                                      }}
                                      placeholder="アレルギーの詳細を入力"
                                      className="mt-2 rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                                    />
                                  </FormControl>
                                </FormItem>
                              )}
                            />
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

              {/* 二次会参加確認 */}
              <FormField
                control={form.control}
                name="afterPartyAttendance"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>二次会への出欠 <span className="text-red-500">*</span></FormLabel>
                    <div className="text-sm text-gray-600 mb-2">
                      ※ 参加費、場所は未定（那覇付近予定）
                    </div>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        className="flex flex-wrap gap-2"
                      >
                        <label className="flex items-center space-x-3 cursor-pointer min-h-[44px] py-2 px-3 rounded-lg hover:bg-white/20 transition-colors">
                          <RadioGroupItem value="yes" />
                          <span className="font-normal">出席</span>
                        </label>
                        <label className="flex items-center space-x-3 cursor-pointer min-h-[44px] py-2 px-3 rounded-lg hover:bg-white/20 transition-colors">
                          <RadioGroupItem value="no" />
                          <span className="font-normal">欠席</span>
                        </label>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 二次会参加人数 */}
              {form.watch('afterPartyAttendance') === 'yes' && (
                <FormField
                  control={form.control}
                  name="afterPartyCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>二次会参加人数</FormLabel>
                      <Select onValueChange={(value) => field.onChange(Number(value))} defaultValue={String(field.value)}>
                        <FormControl>
                          <SelectTrigger className="rounded-full border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <SelectItem key={num} value={String(num)}>{num}名</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>お祝いメッセージ</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="新郎新婦へのメッセージをどうぞ"
                        className="rounded-2xl border-2 border-pastel-purple/30 focus:border-pastel-purple bg-white"
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 画像アップロードセクション */}
              <div className="space-y-2">
                <FormLabel>思い出の写真（任意）</FormLabel>
                <p className="text-sm text-gray-500">
                  新郎新婦との思い出の写真をアップロードできます（最大5枚）
                </p>
                <ImageUpload
                  maxImages={5}
                  onFilesChange={(files) => {
                    setImageFiles(files)
                  }}
                  className="mt-3"
                />
                {/* アップロード進捗表示 */}
                {uploadingImages && imageFiles.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-sm font-medium">アップロード中...</p>
                    {imageFiles.map((file, index) => {
                      const progress = uploadProgress[index] || 0
                      return (
                        <div key={file.id} className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="truncate max-w-[200px]">{file.file.name}</span>
                            <span>{progress}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                              className="bg-pastel-purple h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-center pb-4">
              <Button
                type="submit"
                disabled={loading}
                className={cn(
                  "bg-white/80 backdrop-blur-sm border-2 border-pastel-purple text-gray-700 px-6 py-3 rounded-full shadow-lg hover:shadow-xl hover:scale-105 font-fairy",
                  "hover:bg-white/90",
                  loading && "opacity-50 cursor-not-allowed"
                )}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {uploadingImages ? '画像アップロード中...' : '送信中...'}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    送信する
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
